/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import Select from '@ba-ui-toolkit/select';

import styles from './SelectGroup.scss';

const { sanitize, getInheritedProps } = propsUtils;

const selectGroupClassName = styles.selectGroupClassName.replace(/"/g, '');

export default class SelectGroup extends Component {
	static __SelectGroup__ = true;

	static propTypes = {
		...sanitize(Select.propTypes, null, 'id'),
		/** Passed as a prop to each select component */
		active: PropTypes.bool,
		/** Sets allignment to left or right */
		align: PropTypes.oneOf(['left', 'right']),
		/** Custom class name(s) */
		className: PropTypes.string,
		/** Not used, will be depricated soon */
		disabled: PropTypes.bool,
		/** Passed as a prop to each select component */
		hover: PropTypes.bool,
		/** Unique ID for each select component */
		id: PropTypes.string,
		/** ID of the last Select component */
		lastId: PropTypes.string,
		/** Callback function on selection change */
		onChange: PropTypes.func.isRequired,
		/** Contains a hierarchy of items for all select components */
		options: PropTypes.object.isRequired,
		/** Not used, will be depricated soon */
		readOnly: PropTypes.bool,
		/** Sets the array of selected values */
		selected: PropTypes.array.isRequired,
		/** Not used, will be depricated soon */
		size: PropTypes.string,
		/** Custom CSS styling */
		style: PropTypes.object,
	};

	static defaultProps = {
		className: '',
		style: {},
	};

	_onChange(v, i) {
		const { onChange, selected } = this.props;
		if (onChange) {
			const value = selected.slice(0, i);
			value[i] = v;
			onChange(value, i);
		}
	}

	_createSelects(selects, options, i) {
		const { lastId, selected, hover, active, ...others } = this.props;
		const selectPassthroughProps = getInheritedProps(others, Select);
		options.items.forEach(option => {
			if (option.children && selected[i] === option.value) {
				this._createSelects(selects, option.children, i + 1);
			}
		});

		if (options.items.length) {
			const isLast = selects.length === 0;
			selects.unshift(
				<div
					className={`${selectGroupClassName}__item`}
					data-tid={`ba-common-select-group__select-${i}`}
					key={i}
				>
					<Select
						{...selectPassthroughProps}
						active={isLast && active}
						hover={isLast && hover}
						id={isLast ? lastId : null}
						label={options.groupLabel}
						onChange={v => this._onChange(v, i)}
						options={options.items}
						selected={selected[i] ? selected[i] : ''}
					/>
				</div>
			);
		}
	}

	_createAllSelects() {
		const { options } = this.props;
		const selects = [];
		this._createSelects(selects, options, 0);

		return selects;
	}

	render() {
		const { align, className, id, style, ...others } = this.props;

		const classNames = cx(selectGroupClassName, align);

		return (
			<div
				className={`${classNames} ${className}`}
				id={id}
				style={style}
				{...sanitize(others, SelectGroup)}
			>
				{this._createAllSelects()}
			</div>
		);
	}
}
